import { FlexibleConnectedPositionStrategy, Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
    ComponentRef,
    Directive,
    ElementRef,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    TemplateRef,
} from '@angular/core';
import { CustomToolTipComponent } from '../components/custom-tool-tip/custom-tool-tip.component';

@Directive({
    selector: '[customToolTip]',
    exportAs:'customToolTip'
})
export class ToolTipRendererDirective implements OnChanges, OnDestroy {
    /**
     * This will be used to show tooltip or not
     * This can be used to show the tooltip conditionally
     */
    @Input() showToolTip: boolean = true;

    //If this is specified then the specified text will be shown in the tooltip
    @Input(`customToolTip`) text?: string;

    //If this is specified then specified template will be rendered in the tooltip
    @Input() contentTemplate!: TemplateRef<any>;

    @Input() customTooltipDisabled!: boolean;

    @Input() originX: any = 'center';
    @Input() originY: any = 'bottom';
    @Input() overlayX: any = 'center';
    @Input() overlayY: any = 'top';
    @Input() offsetY: number = 5;

    @Input() classList?: string;
    @Input() horizontalAnim: 'left' | 'center' | 'right' = 'center';
    @Input() verticalAnim: 'top' | 'center' | 'bottom' = 'center';

    @Input() manual: boolean = false;

    private _overlayRef?: OverlayRef;
    private _positionStrategy?: FlexibleConnectedPositionStrategy;

    private tooltipRef?: ComponentRef<CustomToolTipComponent>;

    constructor(
        private _overlay: Overlay,
        private _overlayPositionBuilder: OverlayPositionBuilder,
        private _elementRef: ElementRef
    ) {}

    /**
     * Init life cycle event handler
     */
    ngOnInit() {
        if (!this.showToolTip) {
            return;
        }

        //you can take the position as an input to adjust the position
        //, for now, it will show at the bottom always; but you can adjust your code
        //  as per your need
        this._positionStrategy = this._overlayPositionBuilder.flexibleConnectedTo(this._elementRef).withPositions([
            {
                originX: this.originX,
                originY: this.originY,
                overlayX: this.overlayX,
                overlayY: this.overlayY,
                offsetY: this.offsetY,
            },
        ]);

        //this._overlayRef = this._overlay.create({ positionStrategy: this._positionStrategy });
    }

    /**
     * This method will be called whenever the mouse enters in the Host element
     * i.e. where this directive is applied
     * This method will show the tooltip by instantiating the CustomToolTipComponent and attaching to the overlay
     */
    @HostListener('mouseenter')
    onMouseEnter() {
        if(!this.manual)
            this.show();
    }

    show() {
        //attach the component if it has not already attached to the overlay
        if (!this._overlayRef) {
            this._overlayRef = this._overlay.create({ positionStrategy: this._positionStrategy });
        }
        if (!this._overlayRef.hasAttached()) {
            this.tooltipRef = this._overlayRef.attach(new ComponentPortal(CustomToolTipComponent));
            if(this.text)
              this.tooltipRef.instance.text = this.text;
            this.tooltipRef.instance.contentTemplate = this.contentTemplate;
            this.tooltipRef.instance.customTooltipDisabled = this.customTooltipDisabled;
            this.tooltipRef.instance.horizontalAnim = this.horizontalAnim;
            this.tooltipRef.instance.verticalAnim = this.verticalAnim;
            if(this.classList)
              this.tooltipRef.instance.classList = this.classList;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['customTooltipDisabled'] && this.tooltipRef) {
            this.tooltipRef.instance.customTooltipDisabled = this.customTooltipDisabled;
        }
    }

    /**
     * This method will be called when the mouse goes out of the host element
     * i.e. where this directive is applied
     * This method will close the tooltip by disposing the overlay from the view
     */
    @HostListener('mouseleave')
    onMouseLeave() {
        if(!this.manual)
            this.hide();
    }

    hide() {
        this.closeToolTip();
    }

    /**
     * Destroy lifecycle event handler
     * This method will make sure to close the tooltip
     */
    ngOnDestroy() {
        this.closeToolTip();
    }

    /**
     * This method will close the tooltip by disposing the component from the overlay
     */
    private closeToolTip() {
        if (this._overlayRef) {
            this._overlayRef.dispose();
            this._overlayRef = undefined;
            //this._overlayRef.detach();
        }
    }
}
